import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API_BASE } from "@/lib/api";
import { CheckCircle, Warning, FilePdf, LockKey } from "@phosphor-icons/react";
import axios from "axios";
import { money } from "@/lib/money";

const fmtHol = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

// Note: sub-components declared at module scope (not inside PublicProposal) to
// avoid re-mount-on-keystroke focus loss. Same pattern as PublicPacket.
const Card = ({ title, subtitle, children }) => (
  <div className="bg-white border border-slate-200 rounded-md p-5 sm:p-6 mb-4">
    <div className="tinylabel text-teal-700">{title}</div>
    {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
    <div className="mt-3">{children}</div>
  </div>
);
const Row = ({ k, v, multiline }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-1 sm:gap-3 py-1.5 border-b border-slate-100 last:border-b-0">
    <div className="text-xs uppercase tracking-widest font-semibold text-slate-500 pt-0.5">{k}</div>
    <div className={`text-slate-800 text-sm ${multiline ? "whitespace-pre-wrap" : ""}`}>{v || "—"}</div>
  </div>
);
const Input = (props) => (
  <input {...props} className={`w-full h-11 px-3 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${props.className || ""}`} />
);
const Field = ({ label, children, full, hint }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</label>
    <div className="mt-1">{children}</div>
    {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
  </div>
);

export default function PublicProposal() {
  const { token } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ accepted_by_name: "", accepted_by_title: "", signature_name: "", consent: false });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    axios.get(`${API_BASE}/public/proposals/${token}`)
      .then((r) => setP(r.data))
      .catch((e) => setErr(e.response?.data?.detail?.toString() || e.message));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!f.consent) return setErr("Please acknowledge the General Terms & Conditions before signing.");
    if (f.signature_name.trim().toLowerCase() !== f.accepted_by_name.trim().toLowerCase()) {
      return setErr("The typed signature must match the printed name above.");
    }
    setBusy(true);
    try {
      const { consent, ...payload } = f;
      await axios.post(`${API_BASE}/public/proposals/${token}/accept`, payload);
      setSent(true);
    } catch (er) {
      setErr(er.response?.data?.detail?.toString() || er.message);
    } finally { setBusy(false); }
  };

  if (err && !p) return <Frame><ErrBox msg={err} /></Frame>;
  if (!p) return <Frame><div className="p-8">Loading…</div></Frame>;
  if (p.expired && p.status === "sent") {
    return <Frame><ErrBox title="Link expired"
      msg="This proposal link has expired. Please contact SOS to have a fresh one sent to you." /></Frame>;
  }
  if (p.already_accepted || sent) {
    return (
      <Frame>
        <div className="max-w-md mx-auto text-center bg-white rounded-md border border-slate-200 p-8">
          <CheckCircle size={56} weight="fill" className="text-teal-600 mx-auto" />
          <h1 className="text-2xl font-black mt-3">Proposal accepted</h1>
          <p className="text-sm text-slate-600 mt-2">Thank you. A signed copy of the proposal has been emailed to you and to Safety Onsite Solutions. Bo Harte will follow up shortly to coordinate next steps.</p>
          <a href={`${API_BASE}/public/proposals/${token}/pdf`} target="_blank" rel="noreferrer"
             className="mt-5 inline-flex items-center gap-2 h-11 px-4 border border-slate-300 rounded-md font-semibold text-sm hover:bg-slate-50">
            <FilePdf size={16} weight="bold" /> Download signed PDF
          </a>
        </div>
      </Frame>
    );
  }

  return (
    <Frame>
      <div className="max-w-3xl mx-auto">
        <Header />

        <Card title="Proposal for Safety Engineering Services"
              subtitle={`Prepared for ${p.company_name}`}>
          <p className="text-slate-700 text-sm leading-relaxed">
            Safety Onsite Solutions, Inc. (SOS) is pleased to provide this proposal for safety
            engineering services. Please review the terms below in full, then sign at the bottom to
            accept.
          </p>
        </Card>

        <Card title="Client &amp; Project">
          <Row k="Client" v={p.company_name} />
          <Row k="Location" v={p.location} />
          <Row k="Description" v={p.description} multiline />
          <Row k="Contact" v={`${p.contact_name} · ${p.contact_email} · ${p.contact_phone}`} />
          <Row k="Anticipated start" v={p.anticipated_start_date} />
          <Row k="Anticipated duration" v={p.anticipated_duration} />
        </Card>

        <Card title="Scope of Work">
          <p className="text-slate-700 text-sm leading-relaxed">
            Beginning {p.anticipated_start_date}, SOS will supply a Safety Professional with the
            education and experience in safety management needed to provide safety coordination
            services in conformance with your workplace safety regimen. Working primarily through
            the designated Safety Professional, SOS will:
          </p>
          <ul className="mt-3 space-y-1.5 text-sm text-slate-700 list-disc list-inside">
            <li>Provide employee safety training for new hires</li>
            <li>Administer your drug screening program</li>
            <li>Provide ongoing daily on-site safety oversight</li>
            <li>Conduct specific hazard training sessions</li>
            <li>Collect data on accidents &amp; investigate causes</li>
            <li>Monitor safety procedure violations</li>
            <li>Outline suggested safety remediation measures</li>
            <li>Oversee implementation of safety remediation (excluding construction) &amp; maintain OSHA-mandated safety logs</li>
          </ul>
        </Card>

        <Card title="Billing &amp; Payment">
          <div className="space-y-3 text-sm text-slate-700 leading-relaxed">
            <p>Billing is based on an hourly rate of <b className="text-slate-900">{money(p.hourly_rate)} / hour</b> for the designated Safety Professional{p.ot_rate_applies && p.ot_rate > 0 ? "" : " (straight time only, regardless of the number of hours worked per week)"}.</p>
            {p.ot_rate_applies && p.ot_rate > 0 && (
              <p data-testid="public-proposal-ot-terms">Hours beyond <b className="text-slate-900">{p.ot_threshold_hours || 40} hours</b> in a single week are billed at an overtime rate of <b className="text-slate-900">{money(p.ot_rate)} / hour</b>.</p>
            )}
            {p.holiday_rate_applies && p.holiday_rate > 0 && (
              <p data-testid="public-proposal-holiday-terms">
                Hours worked on designated project holidays are billed at a holiday rate of <b className="text-slate-900">{money(p.holiday_rate)} / hour</b> (in lieu of, not in addition to, any overtime premium).
                {(p.holiday_dates || []).length > 0 && (
                  <> The following dates are designated as project holidays: <b className="text-slate-900">{p.holiday_dates.map(fmtHol).join(", ")}</b>.</>
                )}
              </p>
            )}
            {p.minimum_hours_sentence && <p>{p.minimum_hours_sentence}</p>}
            <p>{p.per_diem_sentence}</p>
            {p.mobilization_sentence && <p>{p.mobilization_sentence}</p>}
            <p>Client will be invoiced weekly for the prior week's services. Payment is due within thirty (30) days of the invoice date. A 2% discount is available on any invoice paid within ten (10) days of the invoice date; invoices unpaid after thirty (30) days will accrue a late charge of 3.5% per month.</p>
            <p>SOS maintains general liability insurance; a Certificate of Insurance is available upon request.</p>
            {p.special_terms && (
              <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-md">
                <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Special terms &amp; conditions</div>
                <div className="whitespace-pre-wrap">{p.special_terms}</div>
              </div>
            )}
          </div>
        </Card>

        <Card title="General Terms &amp; Conditions">
          <ol className="text-xs sm:text-sm text-slate-700 leading-relaxed space-y-2 list-decimal list-inside">
            <li><b>Scope of Work</b> — SOS works within the client's pre-existing safety protocols, with the goal of monitoring and improving them. Client retains sole responsibility for determining the quantity and nature of the work adequate for the client's intended purpose.</li>
            <li><b>Responsibility</b> — Final approval on any significant proposed modifications to pre-existing protocols will be solely by Client.</li>
            <li><b>Construction</b> — SOS work will not include the supervision of any construction work or the final sign-off on any significant changes.</li>
            <li><b>Accommodation</b> — Client will provide on-site office space for the assigned Safety Professional, as well as any site-peculiar needs (e.g., transport).</li>
            <li><b>Indemnity</b> — Client agrees to indemnify and hold SOS harmless from any and all claims, suits, costs and expenses including attorney fees and court costs arising out of negligence.</li>
            <li><b>Relationships</b> — Client recognizes that SOS devotes substantial resources to the recruitment and training of its stable of subcontract personnel who are assigned Safety Professional roles, and that such personnel are subcontractors rather than employees of SOS.</li>
            <li><b>Placement Fee</b> — Should Client seek to employ or directly contract with an SOS Safety Professional during the first six months of this engagement, Client will promptly pay SOS a placement fee equal to 300 hours at the then-current billing rate of the assigned Safety Professional. After this agreement has been in place for six months, any applicable recruitment fee is waived.</li>
            <li><b>Prompt Payment</b> — SOS reserves the right to discontinue services any time its invoices are not paid promptly.</li>
            <li><b>Governing Law</b> — This agreement is governed by and construed in accordance with the laws of the State of Ohio.</li>
            <li><b>Entire Agreement</b> — This document represents the entire agreement between the parties. There are no representations, warranties, or undertakings made other than those set forth herein. This agreement may be amended or modified only in writing signed by each of the parties hereto. This agreement may be terminated unilaterally by either party on seven (7) days' written notice.</li>
          </ol>
        </Card>

        <Card title="Authorization" subtitle="Sign below to accept this proposal on behalf of your organization.">
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Company name">
              <Input data-testid="prop-accept-company" value={p.company_name} readOnly className="bg-slate-50" />
            </Field>
            <Field label="Printed name">
              <Input required data-testid="prop-accepted_by_name" value={f.accepted_by_name} onChange={(e) => set("accepted_by_name", e.target.value)} placeholder="Your full name" />
            </Field>
            <Field label="Position / Title">
              <Input required data-testid="prop-accepted_by_title" value={f.accepted_by_title} onChange={(e) => set("accepted_by_title", e.target.value)} placeholder="e.g. Project Manager" />
            </Field>
            <Field label="Date">
              <Input data-testid="prop-accept-date" value={new Date().toLocaleDateString()} readOnly className="bg-slate-50" />
            </Field>
            <Field label="Typed signature" full hint="Must match the printed name above. Treated as legally binding under the U.S. E-SIGN Act.">
              <Input required data-testid="prop-signature_name" value={f.signature_name} onChange={(e) => set("signature_name", e.target.value)} placeholder="Type your full name" />
            </Field>
            <div className="sm:col-span-2 mt-1">
              <label className="flex items-start gap-3 text-sm cursor-pointer">
                <input type="checkbox" data-testid="prop-consent" checked={f.consent}
                  onChange={(e) => set("consent", e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-teal-600" />
                <span className="text-slate-700">
                  I have read the General Terms &amp; Conditions above and I have authority to accept this proposal on behalf of <b>{p.company_name}</b>.
                </span>
              </label>
            </div>
            {err && <div className="sm:col-span-2 text-rose-600 text-sm">{err}</div>}
            <div className="sm:col-span-2 mt-3 flex flex-col-reverse sm:flex-row gap-3 sm:justify-end">
              <a data-testid="prop-download-preview" href={`${API_BASE}/public/proposals/${token}/pdf`} target="_blank" rel="noreferrer"
                 className="h-11 px-4 border border-slate-300 rounded-md font-semibold text-sm inline-flex items-center gap-2 justify-center hover:bg-slate-50">
                <FilePdf size={16} weight="bold" /> Download PDF
              </a>
              <button type="submit" data-testid="prop-accept-btn" disabled={busy}
                      className="h-11 px-6 bg-teal-700 hover:bg-teal-800 text-white font-bold rounded-md disabled:opacity-50 inline-flex items-center gap-2 justify-center">
                <CheckCircle size={16} weight="bold" /> {busy ? "Signing…" : "Accept & Sign Proposal"}
              </button>
            </div>
            <p className="sm:col-span-2 text-xs text-slate-500 flex items-start gap-2 mt-2">
              <LockKey size={14} className="mt-0.5" /> This link is private to your organization. Your signature is captured with a timestamp and IP address for audit purposes.
            </p>
          </form>
        </Card>
      </div>
    </Frame>
  );
}

const Frame = ({ children }) => (
  <div className="min-h-screen bg-slate-50 py-6 sm:py-10 px-4">{children}</div>
);
const Header = () => (
  <div className="max-w-3xl mx-auto mb-6 flex items-center justify-between border-b border-slate-200 pb-4 gap-3">
    <div className="flex items-center gap-3 min-w-0">
      <img
        src={`${API_BASE}/settings/company/logo`}
        alt="Safety Onsite Solutions"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
        className="h-12 sm:h-14 w-auto object-contain shrink-0"
      />
      <div className="min-w-0">
        <div className="text-xs font-bold uppercase tracking-[3px] text-teal-700">Safety Onsite Solutions</div>
        <div className="text-lg sm:text-xl font-black text-slate-900 mt-0.5 truncate">Proposal for Safety Engineering Services</div>
      </div>
    </div>
    <div className="text-right text-xs text-slate-500 hidden sm:block shrink-0">
      P.O. Box 297, Ironton, OH 45638<br />
      Office: 740-532-0600
    </div>
  </div>
);
const ErrBox = ({ title = "Link unavailable", msg }) => (
  <div className="max-w-md mx-auto bg-white rounded-md border border-slate-200 p-8 text-center">
    <Warning size={44} weight="fill" className="text-rose-600 mx-auto" />
    <h1 className="text-xl font-black mt-3">{title}</h1>
    <p className="text-sm text-slate-600 mt-2">{msg}</p>
  </div>
);
